import { FormControl } from '@material-ui/core'
import { navigate } from 'gatsby'
import { TFunction } from 'i18next'
import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import Select from 'react-select'
import axios from '../../axios'
import { ENDPOINTS, ROUTES } from '../../constants'
import { styles } from './styles'

interface BrandCategory {
  id: number
  name: string
  isVisibleOnWebsite: boolean
  position: number
}

interface BrandsCategorySortProps {
  selectedFilter: number | string | null
  onFilterChange: (value: number | string | null) => void
  t: TFunction
}

const BrandsCategorySort = ({
  t,
  onFilterChange,
  selectedFilter,
  ...props
}: BrandsCategorySortProps) => {
  const classes = styles()
  const [categories, setCategories] = useState<BrandCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedOption, setSelectedOption] = useState<{
    value: number | string | null
    label: string
  }>({
    value: null,
    label: t('shared.all'),
  })
  const [hasFilterChanged, setHasFilterChanged] = useState(false)

  // Fetch categories from API
  useEffect(() => {
    ;(async () => {
      try {
        const response = await axios.get(ENDPOINTS.brandCategories)
        const fetchedCategories = response.data.data || []
        setCategories(fetchedCategories)
        setLoading(false)
      } catch (err) {
        console.error('Failed to fetch brand categories:', err)
        setLoading(false)
      }
    })()
  }, [])

  // Build options from fetched categories
  const options: { value: number | string | null; label: string }[] = [
    { value: null, label: t('shared.all') },
    ...categories.map(cat => ({
      value: cat.id,
      label: cat.name,
    })),
  ]

  const handleChange = (
    category: { value: number | string | null; label: string } | null
  ) => {
    if (!category) return

    setSelectedOption(category)
    setHasFilterChanged(prevState => !prevState)
    const params = new URLSearchParams(location.search)
    if (params.has('category')) {
      params.delete('category')
    }
    if (category.value !== null) {
      // Use category ID in URL for better accuracy
      params.append('category', String(category.value))
    }
    navigate(`${ROUTES.brands}?${params.toString()}`)
    onFilterChange(category.value)
  }

  useEffect(() => {
    if (loading || categories.length === 0) return

    const url = new URL(location.href)
    const categoryParam = url.searchParams.get('category')
    if (categoryParam) {
      // Parse category ID from URL
      const categoryId = parseInt(categoryParam, 10)
      if (!Number.isNaN(categoryId)) {
        const category = categories.find(cat => cat.id === categoryId)
        if (category) {
          const categoryOption = { value: category.id, label: category.name }
          setSelectedOption(categoryOption)
          onFilterChange(category.id)
        }
      }
    } else {
      // Reset to "all" if no category in URL
      setSelectedOption({ value: null, label: t('shared.all') })
      onFilterChange(null)
    }
  }, [hasFilterChanged, categories, loading])

  const colourStyles = {
    control: styles => ({
      ...styles,
      backgroundColor: 'white',
      width: '210px',
      padding: 0,
      marginLeft: 10,
      cursor: 'pointer',
      border: 'none !important',
      boxShadow: 'none',
      '&:hover': {
        border: 'none !important',
      },
    }),

    option: styles => {
      return {
        ...styles,
        fontFamily: 'Schnyder M Light',
        fontSize: 30,
        padding: '0 0 0 10px',
      }
    },

    valueContainer: styles => ({
      height: 50,
    }),

    singleValue: styles => ({
      ...styles,
      top: 'unset',
      bottom: -25,
      fontFamily: 'Schnyder M Light',
      fontSize: '1.875rem',
      fontWeight: 400,
    }),

    placeholder: styles => ({
      display: 'none',
    }),

    input: styles => ({
      color: 'transparent',
    }),

    menu: styles => ({
      ...styles,
      width: 250,
    }),
  }

  if (loading) {
    return (
      <div>
        <FormControl className={classes.formControl}>
          <h4 className={classes.sortByText}>{t('shared.category')}: </h4>
          <div style={{ width: '210px', height: '50px' }} />
        </FormControl>
      </div>
    )
  }

  return (
    <div>
      <FormControl className={classes.formControl}>
        <h4 className={classes.sortByText}>{t('shared.category')}: </h4>
        <Select
          value={selectedOption}
          className={classes.selectList}
          onChange={handleChange}
          options={options}
          styles={colourStyles}
          components={{
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
          }}
          isSearchable={false}
        />
      </FormControl>
    </div>
  )
}

export default withTranslation()(BrandsCategorySort)
